import { useState, useRef, useCallback } from 'react';
import { validateChatMessage } from '../../services/validationService';
import { useTranslate } from '../../lib/i18n';

interface UseMessageInputProps {
  onSend: (message: string, abortSignal?: AbortSignal) => Promise<void>;
  isOperatorChat?: boolean;
}

export const useMessageInput = ({ onSend, isOperatorChat = true }: UseMessageInputProps) => {
  const [message, setMessage] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const abortController = useRef<AbortController | null>(null);

  const t = useTranslate();

  // Handle message change
  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
      if (validationError) {
        setValidationError(null);
      }
    },
    [validationError]
  );

  // Handle message send with validation
  const handleSend = useCallback(async () => {
    if (isSendingMessage) return;

    // Clear validation error
    setValidationError(null);

    // Validate the message
    const validation = validateChatMessage(message, isOperatorChat);
    if (!validation.success) {
      if (validation.error?.includes('||')) {
        const [key, char] = validation.error.split('||');
        setValidationError(t(key || '', { char }));
      } else {
        setValidationError(t(validation.error ?? 'aiChatbot.unknownError'));
      }
      return;
    }

    // Create abort controller for cancellable request
    const controller = new AbortController();
    abortController.current = controller;

    // Set sending state
    setIsSendingMessage(true);

    // Clear the message input immediately (optimistically)
    setMessage('');

    try {
      // Send the message through the provided callback with abort signal
      await onSend(message, controller.signal);
    } catch (error) {
      // Don't handle errors if we aborted the request
      if (controller.signal.aborted) {
        return;
      }
      throw error;
    } finally {
      // Only clean up if the request wasn't aborted
      if (abortController.current === controller) {
        setIsSendingMessage(false);
        abortController.current = null;
      }
    }
  }, [message, isSendingMessage, isOperatorChat, onSend, t]);

  // Handle message cancel
  const handleCancel = useCallback(() => {
    if (abortController.current) {
      // Abort the request
      abortController.current.abort();
      abortController.current = null;
      setIsSendingMessage(false);
    }
  }, []);

  // Handle key press events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend]
  );

  return {
    message,
    setMessage,
    validationError,
    isSendingMessage,
    handleMessageChange,
    handleSend,
    handleCancel,
    handleKeyDown,
  };
};
